import {
  RawGaushala,
  NormalizedGaushala,
  DataSource,
  GeocodingResult,
  INDIA_CENTER,
} from '../types/gaushalaMap.types';

/**
 * Data Transform Service
 * Converts raw gaushala data from API into normalized format
 * guaranteed to have latitude and longitude
 */

/**
 * Map of Indian states to their center coordinates
 * Used for better fallback placement instead of India center
 */
const STATE_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  'Andhra Pradesh': { latitude: 15.9129, longitude: 78.6675 },
  'Arunachal Pradesh': { latitude: 28.2180, longitude: 94.7278 },
  'Assam': { latitude: 26.2006, longitude: 92.9376 },
  'Bihar': { latitude: 25.5941, longitude: 85.1376 },
  'Chhattisgarh': { latitude: 21.2787, longitude: 81.8661 },
  'Goa': { latitude: 15.2993, longitude: 73.8243 },
  'Gujarat': { latitude: 22.2587, longitude: 71.1924 },
  'Haryana': { latitude: 29.0588, longitude: 77.0745 },
  'Himachal Pradesh': { latitude: 31.7433, longitude: 77.1205 },
  'Jammu & Kashmir': { latitude: 33.7782, longitude: 76.5769 },
  'Jharkhand': { latitude: 23.6102, longitude: 85.2799 },
  'Karnataka': { latitude: 15.3173, longitude: 75.7139 },
  'Kerala': { latitude: 10.8505, longitude: 76.2711 },
  'Ladakh': { latitude: 34.1526, longitude: 77.5770 },
  'Madhya Pradesh': { latitude: 22.9734, longitude: 78.6569 },
  'Maharashtra': { latitude: 19.7515, longitude: 75.7139 },
  'Manipur': { latitude: 24.6637, longitude: 93.9063 },
  'Meghalaya': { latitude: 25.4670, longitude: 91.3662 },
  'Mizoram': { latitude: 23.1815, longitude: 92.9789 },
  'Nagaland': { latitude: 26.1584, longitude: 94.5624 },
  'Odisha': { latitude: 20.9517, longitude: 85.0985 },
  'Punjab': { latitude: 31.5497, longitude: 74.3436 },
  'Rajasthan': { latitude: 27.0238, longitude: 74.2179 },
  'Sikkim': { latitude: 27.5330, longitude: 88.5122 },
  'Tamil Nadu': { latitude: 11.1271, longitude: 79.280 },
  'Telangana': { latitude: 18.1124, longitude: 79.0193 },
  'Tripura': { latitude: 23.4408, longitude: 91.9882 },
  'Uttar Pradesh': { latitude: 26.8467, longitude: 80.9462 },
  'Uttarakhand': { latitude: 30.0668, longitude: 79.0193 },
  'West Bengal': { latitude: 24.6735, longitude: 88.3063 },
  'Chandigarh': { latitude: 30.7333, longitude: 76.7794 },
  'Delhi': { latitude: 28.7041, longitude: 77.1025 },
  'Puducherry': { latitude: 11.9416, longitude: 79.5853 },
  'Daman & Diu': { latitude: 20.6667, longitude: 72.8333 },
  'Dadra & Nagar Haveli': { latitude: 20.1809, longitude: 73.7997 },
};

/**
 * List of recognized Indian cities and districts
 * Includes common misspellings from database (e.g., "bhimqvaram", "vishakapatnam")
 * Used to validate if an address is real or gibberish
 */
const RECOGNIZED_INDIAN_PLACES: string[] = [
  // Telangana cities and areas (including Hyderabad localities)
  'Hyderabad', 'Secunderabad', 'Begumpet', 'SP Road', 'S P Road', 'Lakshmi Building',
  'Banjara Hills', 'Jubilee Hills', 'Hitech City', 'Hi-Tech City', 'Gachibowli',
  'Madhapur', 'Kukatpally', 'Miyapur', 'Ameerpet', 'SR Nagar', 'Somajiguda',
  'Punjagutta', 'Erragadda', 'Malkajgiri', 'Uppal', 'LB Nagar', 'Dilsukhnagar',
  'Charminar', 'Abids', 'Koti', 'Nampally', 'Lakdi Ka Pul', 'Paradise', 'Patny',
  'Ongole', 'Kamareddy', 'Nizamabad',
  'Karimnagar', 'Warangal', 'Medak', 'Sangareddy', 'Sanagareddy', 'Siddipet',
  'Khammam', 'Nalgonda', 'Adilabad', 'Mancherial', 'Mahbubnagar', 'Rangareddy',

  // Andhra Pradesh cities (including misspellings)
  'Bhimavaram', 'Bhimqvaram', 'Vijayawada', 'Visakhapatnam', 'Vishakapatnam',
  'Vishakhapatnam', 'Tirupati', 'Guntur', 'Nellore', 'Kakinada', 'Rajahmundry', 'Eluru',

  // Karnataka - Bangalore localities
  'Bangalore', 'Bengaluru', 'Koramangala', 'Indiranagar', 'Whitefield',
  'Electronic City', 'HSR Layout', 'Jayanagar', 'JP Nagar', 'BTM Layout',
  'Marathahalli', 'Bannerghatta', 'MG Road', 'Brigade Road', 'Rajajinagar',
  'Malleswaram', 'Yelahanka', 'Hebbal', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum',

  // Tamil Nadu - Chennai localities
  'Chennai', 'Madras', 'T Nagar', 'Anna Nagar', 'Adyar', 'Velachery', 'Tambaram',
  'Egmore', 'Mylapore', 'Porur', 'Guindy', 'Nungambakkam', 'Mount Road',
  'Coimbatore', 'RS Puram', 'Gandhipuram', 'Salem', 'Madurai', 'Trichy', 'Tiruchirappalli',

  // Maharashtra - Mumbai localities
  'Mumbai', 'Andheri', 'Andheri East', 'Andheri West', 'Bandra', 'Bandra West',
  'Colaba', 'Dadar', 'Borivali', 'Malad', 'Goregaon', 'Powai', 'Kurla',
  'Thane', 'Navi Mumbai', 'Vashi', 'Lower Parel', 'Worli', 'Marine Drive', 'Juhu',
  'Pune', 'Kothrud', 'Hinjewadi', 'Wakad', 'Baner', 'Viman Nagar', 'Kalyani Nagar',
  'Koregaon Park', 'Hadapsar', 'Magarpatta', 'Nagpur', 'Aurangabad', 'Nashik',

  // Delhi NCR localities
  'Delhi', 'New Delhi', 'Connaught Place', 'CP', 'Karol Bagh', 'Rajouri Garden',
  'Dwarka', 'Rohini', 'Pitampura', 'Lajpat Nagar', 'Saket', 'Vasant Kunj',
  'Noida', 'Greater Noida', 'Sector 62', 'Sector 18', 'Ghaziabad',
  'South Delhi', 'Nehru Place', 'Okhla',

  // Uttar Pradesh localities
  'Lucknow', 'Hazratganj', 'Gomti Nagar', 'Aliganj', 'Indira Nagar Lucknow',
  'Kanpur', 'Civil Lines Kanpur', 'Agra', 'Taj Ganj', 'Varanasi', 'Banaras',
  'Prayagraj', 'Allahabad',

  // West Bengal - Kolkata localities
  'Kolkata', 'Calcutta', 'Salt Lake', 'Sector V', 'New Town', 'Rajarhat',
  'Park Street', 'Esplanade', 'Howrah', 'Dum Dum', 'Ballygunge',

  // Bihar localities
  'Patna', 'Boring Road', 'Kankarbagh', 'Danapur', 'Gaya', 'Muzaffarpur',

  // Jharkhand localities
  'Ranchi', 'Main Road Ranchi', 'Jamshedpur', 'Sakchi', 'Bistupur', 'Dhanbad',

  // Rajasthan localities
  'Jaipur', 'Malviya Nagar Jaipur', 'Vaishali Nagar', 'Mansarovar', 'C Scheme', 'MI Road',
  'Jodhpur', 'Udaipur', 'Ajmer', 'Kota',

  // Gujarat localities
  'Ahmedabad', 'CG Road', 'SG Highway', 'Satellite', 'Navrangpura', 'Prahlad Nagar',
  'Surat', 'Adajan', 'Vesu', 'Vadodara', 'Baroda', 'Alkapuri', 'Rajkot', 'Gandhinagar',

  // Punjab localities
  'Chandigarh', 'Sector 17', 'Sector 35', 'Mohali', 'Panchkula',
  'Amritsar', 'Golden Temple', 'Ludhiana', 'Jalandhar', 'Patiala',

  // Haryana localities
  'Gurgaon', 'Gurugram', 'Cyber City', 'DLF Phase', 'Sohna Road', 'Golf Course Road',
  'MG Road Gurgaon', 'Faridabad', 'Jind', 'Uchana', 'Rohtak', 'Sonipat',
  'Panipat', 'Karnal', 'Hisar', 'Sirsa', 'Ambala', 'Yamunanagar', 'Neemwali',

  // Kerala localities
  'Kochi', 'Cochin', 'Ernakulam', 'Marine Drive Kochi', 'MG Road Kochi', 'Infopark',
  'Thiruvananthapuram', 'Trivandrum', 'Technopark', 'Kozhikode', 'Calicut', 'Thrissur',

  // Goa localities
  'Goa', 'Panaji', 'Panjim',

  // Odisha localities
  'Bhubaneswar', 'Cuttack',

  // Assam localities
  'Guwahati',

  // Other major cities
  'Indore', 'Bhopal', 'Kanyakumari',

  // States (for state-level matching)
  'Andhra Pradesh', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Maharashtra',
  'Telangana', 'Madhya Pradesh', 'Rajasthan', 'Punjab', 'West Bengal',
  'Uttar Pradesh', 'Bihar', 'Odisha', 'Jharkhand', 'Assam',
  'Kerala', 'Haryana', 'Himachal Pradesh', 'Jammu', 'Kashmir',
];

/**
 * Validate if address looks like a real Indian location
 * Filters out obvious gibberish and test data
 * STRICT validation: MUST contain a correctly spelled Indian city or state name
 * Misspellings like "bhimqvaram" will be REJECTED
 * EXPORTED for use in geocoding service and other modules
 *
 * NOTE: Addresses CAN start with building numbers like "1-8-448, Lakshmi Building"
 * These are valid Indian address formats and should NOT be rejected
 */
export const isValidAddress = (address: string | null): boolean => {
  if (!address) return false;

  const cleanAddress = address.trim();
  if (cleanAddress.length < 2) return false;

  const addressLower = cleanAddress.toLowerCase();

  // REJECT: Obvious gibberish and test patterns
  // NOTE: "123 Test Road" or "456 Proxy Ave" patterns - check for test/proxy words
  const testPatterns = ['test', 'proxy', 'fdsa', 'jkfdhjk', 'qwerty', 'sample', 'demo', 'example', 'fake', 'dummy'];
  for (const pattern of testPatterns) {
    if (addressLower.includes(pattern)) {
      return false;
    }
  }

  // REJECT: Addresses that are just numbers (no text)
  if (/^\d+$/.test(addressLower.replace(/[\s\-,|]/g, ''))) {
    return false;
  }

  // REJECT: Addresses that say "not provided"
  if (addressLower === 'address not provided') {
    return false;
  }

  // ACCEPT: Addresses that contain a recognized Indian city or state name
  // This allows building numbers like "1-8-448, Lakshmi Building | Begumpet | Secunderabad"
  for (const place of RECOGNIZED_INDIAN_PLACES) {
    if (addressLower.includes(place.toLowerCase())) {
      return true;
    }
  }

  // REJECT: If no recognized Indian place found, reject the address
  return false;
};

/**
 * Get coordinates for a state
 * Returns state coordinates or India center as fallback
 */
const getStateCoordinates = (stateName: string): { latitude: number; longitude: number } => {
  if (stateName === 'Unknown' || !stateName) {
    return INDIA_CENTER;
  }

  // Try exact match first
  if (STATE_COORDINATES[stateName]) {
    return STATE_COORDINATES[stateName];
  }

  // Try case-insensitive match
  for (const [state, coords] of Object.entries(STATE_COORDINATES)) {
    if (state.toLowerCase() === stateName.toLowerCase()) {
      return coords;
    }
  }

  // Return India center if state not found
  return INDIA_CENTER;
};

/**
 * Exact coordinates for Indian cities and specific areas
 * Used for precise location placement instead of state centers
 * Includes specific areas like SP Road, Begumpet for detailed addresses
 */
const CITY_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  // Telangana cities - Hyderabad/Secunderabad areas (detailed)
  'hyderabad': { latitude: 17.3850, longitude: 78.4867 },
  'secunderabad': { latitude: 17.4399, longitude: 78.4983 },
  'begumpet': { latitude: 17.4432, longitude: 78.4648 },
  'sp road': { latitude: 17.4428, longitude: 78.4975 }, // SP Road in Secunderabad
  's p road': { latitude: 17.4428, longitude: 78.4975 }, // SP Road alternate
  'lakshmi building': { latitude: 17.4430, longitude: 78.4978 }, // Lakshmi Building, SP Road
  'banjara hills': { latitude: 17.4156, longitude: 78.4347 },
  'jubilee hills': { latitude: 17.4325, longitude: 78.4072 },
  'hitech city': { latitude: 17.4435, longitude: 78.3772 },
  'hi-tech city': { latitude: 17.4435, longitude: 78.3772 },
  'gachibowli': { latitude: 17.4401, longitude: 78.3489 },
  'madhapur': { latitude: 17.4483, longitude: 78.3915 },
  'kukatpally': { latitude: 17.4947, longitude: 78.3996 },
  'miyapur': { latitude: 17.4964, longitude: 78.3531 },
  'ameerpet': { latitude: 17.4375, longitude: 78.4483 },
  'sr nagar': { latitude: 17.4403, longitude: 78.4404 },
  'somajiguda': { latitude: 17.4241, longitude: 78.4539 },
  'punjagutta': { latitude: 17.4286, longitude: 78.4518 },
  'erragadda': { latitude: 17.4547, longitude: 78.4358 },
  'malkajgiri': { latitude: 17.4560, longitude: 78.5277 },
  'uppal': { latitude: 17.3978, longitude: 78.5584 },
  'lb nagar': { latitude: 17.3493, longitude: 78.5509 },
  'dilsukhnagar': { latitude: 17.3688, longitude: 78.5247 },
  'charminar': { latitude: 17.3616, longitude: 78.4747 },
  'abids': { latitude: 17.3927, longitude: 78.4760 },
  'koti': { latitude: 17.3860, longitude: 78.4864 },
  'nampally': { latitude: 17.3869, longitude: 78.4696 },
  'lakdi ka pul': { latitude: 17.4053, longitude: 78.4669 },
  'paradise': { latitude: 17.4441, longitude: 78.4820 }, // Paradise area in Secunderabad
  'patny': { latitude: 17.4375, longitude: 78.4892 }, // Patny center area
  'kamareddy': { latitude: 18.3162, longitude: 78.3389 },
  'nizamabad': { latitude: 19.2741, longitude: 78.1091 },
  'karimnagar': { latitude: 18.4386, longitude: 78.1343 },
  'warangal': { latitude: 17.9689, longitude: 78.6139 },
  'medak': { latitude: 18.7421, longitude: 78.2491 },
  'sangareddy': { latitude: 17.6189, longitude: 78.0892 },
  'sanagareddy': { latitude: 17.6189, longitude: 78.0892 }, // alternate spelling
  'siddipet': { latitude: 18.1017, longitude: 78.8522 },
  'khammam': { latitude: 17.2473, longitude: 80.1514 },
  'nalgonda': { latitude: 17.0575, longitude: 79.2669 },
  'adilabad': { latitude: 19.6641, longitude: 78.5320 },
  'mancherial': { latitude: 18.8700, longitude: 79.4580 },
  'mahbubnagar': { latitude: 16.7488, longitude: 77.9850 },
  'rangareddy': { latitude: 17.2543, longitude: 78.3116 },
  'ongole': { latitude: 14.4426, longitude: 79.6304 },

  // Andhra Pradesh cities
  'bhimavaram': { latitude: 16.5500, longitude: 81.5233 },
  'bhimqvaram': { latitude: 16.5500, longitude: 81.5233 }, // misspelling variant
  'vijayawada': { latitude: 16.5062, longitude: 80.6437 },
  'visakhapatnam': { latitude: 17.6869, longitude: 83.2185 },
  'vishakapatnam': { latitude: 17.6869, longitude: 83.2185 }, // misspelling variant
  'vishakhapatnam': { latitude: 17.6869, longitude: 83.2185 }, // misspelling variant
  'tirupati': { latitude: 13.1939, longitude: 79.8941 },
  'guntur': { latitude: 16.3067, longitude: 80.4365 },
  'nellore': { latitude: 14.4426, longitude: 79.9865 },
  'kakinada': { latitude: 16.9891, longitude: 82.2475 },
  'rajahmundry': { latitude: 17.0005, longitude: 81.8040 },
  'eluru': { latitude: 16.7107, longitude: 81.0952 },

  // Karnataka - Bangalore localities
  'bangalore': { latitude: 12.9716, longitude: 77.5946 },
  'bengaluru': { latitude: 12.9716, longitude: 77.5946 },
  'koramangala': { latitude: 12.9279, longitude: 77.6271 },
  'indiranagar': { latitude: 12.9784, longitude: 77.6408 },
  'whitefield': { latitude: 12.9698, longitude: 77.7500 },
  'electronic city': { latitude: 12.8399, longitude: 77.6770 },
  'hsr layout': { latitude: 12.9116, longitude: 77.6389 },
  'jayanagar': { latitude: 12.9308, longitude: 77.5838 },
  'jp nagar': { latitude: 12.9063, longitude: 77.5857 },
  'btm layout': { latitude: 12.9166, longitude: 77.6101 },
  'marathahalli': { latitude: 12.9591, longitude: 77.6974 },
  'bannerghatta': { latitude: 12.8698, longitude: 77.5970 },
  'mg road': { latitude: 12.9756, longitude: 77.6069 },
  'brigade road': { latitude: 12.9716, longitude: 77.6070 },
  'rajajinagar': { latitude: 12.9913, longitude: 77.5520 },
  'malleswaram': { latitude: 13.0036, longitude: 77.5657 },
  'yelahanka': { latitude: 13.1007, longitude: 77.5963 },
  'hebbal': { latitude: 13.0358, longitude: 77.5970 },
  'mysore': { latitude: 12.2958, longitude: 76.6394 },
  'mangalore': { latitude: 12.8628, longitude: 74.8455 },
  'hubli': { latitude: 15.3647, longitude: 75.1240 },
  'belgaum': { latitude: 15.8497, longitude: 74.4977 },

  // Tamil Nadu - Chennai localities
  'chennai': { latitude: 13.0827, longitude: 80.2707 },
  'madras': { latitude: 13.0827, longitude: 80.2707 },
  't nagar': { latitude: 13.0418, longitude: 80.2341 },
  'anna nagar': { latitude: 13.0850, longitude: 80.2101 },
  'adyar': { latitude: 13.0012, longitude: 80.2565 },
  'velachery': { latitude: 12.9815, longitude: 80.2180 },
  'tambaram': { latitude: 12.9249, longitude: 80.1000 },
  'egmore': { latitude: 13.0732, longitude: 80.2609 },
  'mylapore': { latitude: 13.0368, longitude: 80.2676 },
  'porur': { latitude: 13.0382, longitude: 80.1565 },
  'guindy': { latitude: 13.0067, longitude: 80.2206 },
  'nungambakkam': { latitude: 13.0569, longitude: 80.2425 },
  'mount road': { latitude: 13.0618, longitude: 80.2674 },
  'coimbatore': { latitude: 11.0081, longitude: 76.9558 },
  'rs puram': { latitude: 11.0107, longitude: 76.9544 },
  'gandhipuram': { latitude: 11.0168, longitude: 76.9558 },
  'salem': { latitude: 11.6643, longitude: 78.1460 },
  'madurai': { latitude: 9.9252, longitude: 78.1198 },
  'trichy': { latitude: 10.7905, longitude: 78.7047 },
  'tiruchirappalli': { latitude: 10.7905, longitude: 78.7047 },

  // Maharashtra - Mumbai localities
  'mumbai': { latitude: 19.0760, longitude: 72.8777 },
  'andheri': { latitude: 19.1136, longitude: 72.8697 },
  'andheri east': { latitude: 19.1197, longitude: 72.8533 },
  'andheri west': { latitude: 19.1285, longitude: 72.8360 },
  'bandra': { latitude: 19.0596, longitude: 72.8295 },
  'bandra west': { latitude: 19.0596, longitude: 72.8295 },
  'colaba': { latitude: 18.9067, longitude: 72.8147 },
  'dadar': { latitude: 19.0178, longitude: 72.8478 },
  'borivali': { latitude: 19.2307, longitude: 72.8567 },
  'malad': { latitude: 19.1874, longitude: 72.8484 },
  'goregaon': { latitude: 19.1663, longitude: 72.8526 },
  'powai': { latitude: 19.1176, longitude: 72.9060 },
  'kurla': { latitude: 19.0726, longitude: 72.8793 },
  'thane': { latitude: 19.2183, longitude: 72.9781 },
  'navi mumbai': { latitude: 19.0330, longitude: 73.0297 },
  'vashi': { latitude: 19.0771, longitude: 72.9986 },
  'lower parel': { latitude: 18.9972, longitude: 72.8261 },
  'worli': { latitude: 19.0176, longitude: 72.8151 },
  'marine drive': { latitude: 18.9438, longitude: 72.8231 },
  'juhu': { latitude: 19.0883, longitude: 72.8263 },
  'pune': { latitude: 18.5204, longitude: 73.8567 },
  'kothrud': { latitude: 18.5074, longitude: 73.8077 },
  'hinjewadi': { latitude: 18.5912, longitude: 73.7380 },
  'wakad': { latitude: 18.5991, longitude: 73.7640 },
  'baner': { latitude: 18.5590, longitude: 73.7868 },
  'viman nagar': { latitude: 18.5679, longitude: 73.9143 },
  'kalyani nagar': { latitude: 18.5463, longitude: 73.9050 },
  'koregaon park': { latitude: 18.5362, longitude: 73.8939 },
  'hadapsar': { latitude: 18.5089, longitude: 73.9260 },
  'magarpatta': { latitude: 18.5133, longitude: 73.9267 },
  'nagpur': { latitude: 21.1458, longitude: 79.0882 },
  'aurangabad': { latitude: 19.8762, longitude: 75.3433 },
  'nashik': { latitude: 19.9975, longitude: 73.7898 },

  // Delhi NCR localities
  'delhi': { latitude: 28.7041, longitude: 77.1025 },
  'new delhi': { latitude: 28.5355, longitude: 77.2101 },
  'connaught place': { latitude: 28.6315, longitude: 77.2167 },
  'cp': { latitude: 28.6315, longitude: 77.2167 },
  'karol bagh': { latitude: 28.6514, longitude: 77.1907 },
  'rajouri garden': { latitude: 28.6491, longitude: 77.1214 },
  'dwarka': { latitude: 28.5921, longitude: 77.0460 },
  'rohini': { latitude: 28.7495, longitude: 77.0561 },
  'pitampura': { latitude: 28.7007, longitude: 77.1343 },
  'lajpat nagar': { latitude: 28.5687, longitude: 77.2373 },
  'saket': { latitude: 28.5244, longitude: 77.2065 },
  'vasant kunj': { latitude: 28.5172, longitude: 77.1571 },
  'noida': { latitude: 28.5355, longitude: 77.3910 },
  'greater noida': { latitude: 28.4744, longitude: 77.5040 },
  'sector 62': { latitude: 28.6239, longitude: 77.3655 },
  'sector 18': { latitude: 28.5706, longitude: 77.3214 },
  'ghaziabad': { latitude: 28.6692, longitude: 77.4538 },
  'south delhi': { latitude: 28.5245, longitude: 77.2066 },
  'nehru place': { latitude: 28.5491, longitude: 77.2533 },
  'okhla': { latitude: 28.5308, longitude: 77.2710 },

  // Uttar Pradesh localities
  'lucknow': { latitude: 26.8467, longitude: 80.9462 },
  'hazratganj': { latitude: 26.8531, longitude: 80.9463 },
  'gomti nagar': { latitude: 26.8560, longitude: 81.0048 },
  'aliganj': { latitude: 26.8937, longitude: 80.9390 },
  'indira nagar lucknow': { latitude: 26.8772, longitude: 80.9963 },
  'kanpur': { latitude: 26.4499, longitude: 80.3319 },
  'civil lines kanpur': { latitude: 26.4604, longitude: 80.3483 },
  'agra': { latitude: 27.1767, longitude: 78.0081 },
  'taj ganj': { latitude: 27.1631, longitude: 78.0422 },
  'varanasi': { latitude: 25.3176, longitude: 82.9739 },
  'banaras': { latitude: 25.3176, longitude: 82.9739 },
  'prayagraj': { latitude: 25.4358, longitude: 81.8463 },
  'allahabad': { latitude: 25.4358, longitude: 81.8463 },

  // West Bengal - Kolkata localities
  'kolkata': { latitude: 22.5726, longitude: 88.3639 },
  'calcutta': { latitude: 22.5726, longitude: 88.3639 },
  'salt lake': { latitude: 22.5958, longitude: 88.4106 },
  'sector v': { latitude: 22.5726, longitude: 88.4326 },
  'new town': { latitude: 22.5958, longitude: 88.4836 },
  'rajarhat': { latitude: 22.6341, longitude: 88.4637 },
  'park street': { latitude: 22.5514, longitude: 88.3555 },
  'esplanade': { latitude: 22.5645, longitude: 88.3503 },
  'howrah': { latitude: 22.5958, longitude: 88.2636 },
  'dum dum': { latitude: 22.6552, longitude: 88.4268 },
  'ballygunge': { latitude: 22.5299, longitude: 88.3624 },

  // Bihar localities
  'patna': { latitude: 25.5941, longitude: 85.1376 },
  'boring road': { latitude: 25.6114, longitude: 85.1049 },
  'kankarbagh': { latitude: 25.5951, longitude: 85.1670 },
  'danapur': { latitude: 25.6226, longitude: 84.9901 },
  'gaya': { latitude: 24.7914, longitude: 85.0002 },
  'muzaffarpur': { latitude: 26.1209, longitude: 85.3647 },

  // Jharkhand localities
  'ranchi': { latitude: 23.3441, longitude: 85.3096 },
  'main road ranchi': { latitude: 23.3569, longitude: 85.3340 },
  'jamshedpur': { latitude: 22.8046, longitude: 86.1846 },
  'sakchi': { latitude: 22.7998, longitude: 86.1873 },
  'bistupur': { latitude: 22.7868, longitude: 86.1897 },
  'dhanbad': { latitude: 23.7957, longitude: 86.4304 },

  // Rajasthan localities
  'jaipur': { latitude: 26.9124, longitude: 75.7873 },
  'malviya nagar jaipur': { latitude: 26.8530, longitude: 75.8150 },
  'vaishali nagar': { latitude: 26.9134, longitude: 75.7378 },
  'mansarovar': { latitude: 26.8670, longitude: 75.7586 },
  'c scheme': { latitude: 26.9124, longitude: 75.7873 },
  'mi road': { latitude: 26.9124, longitude: 75.7927 },
  'jodhpur': { latitude: 26.2389, longitude: 73.0243 },
  'udaipur': { latitude: 24.5854, longitude: 73.7125 },
  'ajmer': { latitude: 26.4499, longitude: 74.6399 },
  'kota': { latitude: 25.2138, longitude: 75.8648 },

  // Gujarat localities
  'ahmedabad': { latitude: 23.0225, longitude: 72.5714 },
  'cg road': { latitude: 23.0301, longitude: 72.5567 },
  'sg highway': { latitude: 23.0469, longitude: 72.5300 },
  'satellite': { latitude: 23.0120, longitude: 72.5100 },
  'navrangpura': { latitude: 23.0350, longitude: 72.5600 },
  'prahlad nagar': { latitude: 23.0085, longitude: 72.5120 },
  'surat': { latitude: 21.1702, longitude: 72.8311 },
  'adajan': { latitude: 21.1863, longitude: 72.7846 },
  'vesu': { latitude: 21.1502, longitude: 72.7676 },
  'vadodara': { latitude: 22.3072, longitude: 73.1812 },
  'baroda': { latitude: 22.3072, longitude: 73.1812 },
  'alkapuri': { latitude: 22.3103, longitude: 73.1723 },
  'rajkot': { latitude: 22.3039, longitude: 70.8022 },
  'gandhinagar': { latitude: 23.2156, longitude: 72.6369 },

  // Punjab localities
  'chandigarh': { latitude: 30.7333, longitude: 76.7794 },
  'sector 17': { latitude: 30.7415, longitude: 76.7832 },
  'sector 35': { latitude: 30.7230, longitude: 76.7601 },
  'mohali': { latitude: 30.7046, longitude: 76.7179 },
  'panchkula': { latitude: 30.6942, longitude: 76.8606 },
  'amritsar': { latitude: 31.6340, longitude: 74.8723 },
  'golden temple': { latitude: 31.6200, longitude: 74.8765 },
  'ludhiana': { latitude: 30.9010, longitude: 75.8573 },
  'jalandhar': { latitude: 31.3260, longitude: 75.5762 },
  'patiala': { latitude: 30.3398, longitude: 76.3869 },

  // Haryana localities
  'gurgaon': { latitude: 28.4595, longitude: 77.0266 },
  'gurugram': { latitude: 28.4595, longitude: 77.0266 },
  'cyber city': { latitude: 28.4940, longitude: 77.0886 },
  'dlf phase': { latitude: 28.4724, longitude: 77.0930 },
  'sohna road': { latitude: 28.4103, longitude: 77.0446 },
  'golf course road': { latitude: 28.4590, longitude: 77.1025 },
  'mg road gurgaon': { latitude: 28.4799, longitude: 77.0748 },
  'faridabad': { latitude: 28.4089, longitude: 77.3178 },
  'jind': { latitude: 29.3161, longitude: 76.3147 },
  'uchana': { latitude: 29.5217, longitude: 76.2547 },
  'rohtak': { latitude: 28.8955, longitude: 76.6066 },
  'sonipat': { latitude: 28.9931, longitude: 77.0151 },
  'panipat': { latitude: 29.3909, longitude: 76.9635 },
  'karnal': { latitude: 29.6857, longitude: 76.9905 },
  'hisar': { latitude: 29.1492, longitude: 75.7217 },
  'sirsa': { latitude: 29.5342, longitude: 75.0309 },
  'ambala': { latitude: 30.3752, longitude: 76.7821 },
  'yamunanagar': { latitude: 30.1290, longitude: 77.2674 },
  'neemwali': { latitude: 28.9890, longitude: 76.3890 },

  // Kerala localities
  'kochi': { latitude: 9.9312, longitude: 76.2673 },
  'cochin': { latitude: 9.9312, longitude: 76.2673 },
  'ernakulam': { latitude: 9.9816, longitude: 76.2999 },
  'marine drive kochi': { latitude: 9.9816, longitude: 76.2746 },
  'mg road kochi': { latitude: 9.9716, longitude: 76.2885 },
  'infopark': { latitude: 10.0062, longitude: 76.3520 },
  'thiruvananthapuram': { latitude: 8.5241, longitude: 76.9366 },
  'trivandrum': { latitude: 8.5241, longitude: 76.9366 },
  'technopark': { latitude: 8.5570, longitude: 76.8816 },
  'kozhikode': { latitude: 11.2588, longitude: 75.7804 },
  'calicut': { latitude: 11.2588, longitude: 75.7804 },
  'thrissur': { latitude: 10.5276, longitude: 76.2144 },

  // Goa localities
  'goa': { latitude: 15.2993, longitude: 73.8243 },
  'panjim': { latitude: 15.4909, longitude: 73.8278 },
  'panaji': { latitude: 15.4909, longitude: 73.8278 },

  // Odisha cities
  'bhubaneswar': { latitude: 20.2961, longitude: 85.8245 },
  'cuttack': { latitude: 20.4625, longitude: 85.8830 },

  // Jharkhand cities
  'jamshedpur': { latitude: 22.8046, longitude: 86.1846 },

  // Assam cities
  'guwahati': { latitude: 26.1445, longitude: 91.7362 },
};

/**
 * Map of Indian cities to their states
 * Used to identify state from city name
 */
const CITY_TO_STATE: Record<string, string> = {
  // Telangana - Hyderabad/Secunderabad areas
  'hyderabad': 'Telangana',
  'secunderabad': 'Telangana',
  'begumpet': 'Telangana',
  'sp road': 'Telangana',
  's p road': 'Telangana',
  'lakshmi building': 'Telangana',
  'banjara hills': 'Telangana',
  'jubilee hills': 'Telangana',
  'hitech city': 'Telangana',
  'hi-tech city': 'Telangana',
  'gachibowli': 'Telangana',
  'madhapur': 'Telangana',
  'kukatpally': 'Telangana',
  'miyapur': 'Telangana',
  'ameerpet': 'Telangana',
  'sr nagar': 'Telangana',
  'somajiguda': 'Telangana',
  'punjagutta': 'Telangana',
  'erragadda': 'Telangana',
  'malkajgiri': 'Telangana',
  'uppal': 'Telangana',
  'lb nagar': 'Telangana',
  'dilsukhnagar': 'Telangana',
  'charminar': 'Telangana',
  'abids': 'Telangana',
  'koti': 'Telangana',
  'nampally': 'Telangana',
  'lakdi ka pul': 'Telangana',
  'paradise': 'Telangana',
  'patny': 'Telangana',
  'kamareddy': 'Telangana',
  'nizamabad': 'Telangana',
  'karimnagar': 'Telangana',
  'warangal': 'Telangana',
  'medak': 'Telangana',
  'sangareddy': 'Telangana',
  'sanagareddy': 'Telangana', // alternate spelling
  'siddipet': 'Telangana',
  'khammam': 'Telangana',
  'nalgonda': 'Telangana',
  'adilabad': 'Telangana',
  'mancherial': 'Telangana',
  'mahbubnagar': 'Telangana',
  'rangareddy': 'Telangana',
  'ongole': 'Andhra Pradesh',

  // Andhra Pradesh
  'bhimavaram': 'Andhra Pradesh',
  'bhimqvaram': 'Andhra Pradesh', // misspelling variant
  'vijayawada': 'Andhra Pradesh',
  'visakhapatnam': 'Andhra Pradesh',
  'vishakapatnam': 'Andhra Pradesh', // misspelling variant
  'vishakhapatnam': 'Andhra Pradesh', // misspelling variant
  'tirupati': 'Andhra Pradesh',
  'guntur': 'Andhra Pradesh',
  'nellore': 'Andhra Pradesh',
  'kakinada': 'Andhra Pradesh',
  'rajahmundry': 'Andhra Pradesh',
  'eluru': 'Andhra Pradesh',

  // Karnataka
  'bangalore': 'Karnataka',
  'bengaluru': 'Karnataka',
  'mysore': 'Karnataka',
  'mangalore': 'Karnataka',

  // Tamil Nadu
  'chennai': 'Tamil Nadu',
  'madras': 'Tamil Nadu',
  'coimbatore': 'Tamil Nadu',
  'salem': 'Tamil Nadu',

  // Maharashtra
  'mumbai': 'Maharashtra',
  'pune': 'Maharashtra',
  'nagpur': 'Maharashtra',
  'aurangabad': 'Maharashtra',

  // Delhi
  'delhi': 'Delhi',
  'new delhi': 'Delhi',

  // Uttar Pradesh
  'lucknow': 'Uttar Pradesh',
  'kanpur': 'Uttar Pradesh',
  'agra': 'Uttar Pradesh',

  // West Bengal
  'kolkata': 'West Bengal',
  'calcutta': 'West Bengal',

  // Bihar
  'patna': 'Bihar',

  // Rajasthan
  'jaipur': 'Rajasthan',
  'jodhpur': 'Rajasthan',

  // Gujarat
  'ahmedabad': 'Gujarat',
  'surat': 'Gujarat',
  'vadodara': 'Gujarat',

  // Punjab
  'chandigarh': 'Punjab',
  'amritsar': 'Punjab',
  'ludhiana': 'Punjab',

  // Haryana
  'gurgaon': 'Haryana',
  'gurugram': 'Haryana', // alternate name
  'faridabad': 'Haryana',
  'jind': 'Haryana',
  'uchana': 'Haryana',
  'rohtak': 'Haryana',
  'sonipat': 'Haryana',
  'panipat': 'Haryana',
  'karnal': 'Haryana',
  'hisar': 'Haryana',
  'sirsa': 'Haryana',
  'ambala': 'Haryana',
  'yamunanagar': 'Haryana',
  'neemwali': 'Haryana',

  // Goa
  'goa': 'Goa',
  'panaji': 'Goa',

  // Odisha
  'bhubaneswar': 'Odisha',
  'cuttack': 'Odisha',

  // Jharkhand
  'ranchi': 'Jharkhand',
  'jamshedpur': 'Jharkhand',

  // Assam
  'guwahati': 'Assam',
  'assam': 'Assam',
};

/**
 * Extract state from address string
 * Looks for common Indian state names or city names in the address
 * EXPORTED for use in geocoding service fallback logic
 * IMPROVED: Sort cities by length (longest first) to avoid substring mismatches
 */
export const extractStateFromAddress = (address: string | null): string => {
  if (!address) return 'Unknown';

  const addressLowerTrim = address.trim().toLowerCase();

  // FIRST: Check if address is a recognized city and return its state
  if (CITY_TO_STATE[addressLowerTrim]) {
    return CITY_TO_STATE[addressLowerTrim];
  }

  // Check if city is part of a longer address
  // IMPORTANT: Sort by length (longest first) to match more specific cities first
  // This prevents "patna" from matching "visakhapatnam" or other substring issues
  const sortedCities = Object.entries(CITY_TO_STATE).sort((a, b) => b[0].length - a[0].length);
  for (const [city, state] of sortedCities) {
    if (addressLowerTrim.includes(city)) {
      return state;
    }
  }

  const stateAbbreviations: Record<string, string> = {
    AP: 'Andhra Pradesh',
    AR: 'Arunachal Pradesh',
    AS: 'Assam',
    BR: 'Bihar',
    CG: 'Chhattisgarh',
    GA: 'Goa',
    GJ: 'Gujarat',
    HR: 'Haryana',
    HP: 'Himachal Pradesh',
    JK: 'Jammu & Kashmir',
    JH: 'Jharkhand',
    KA: 'Karnataka',
    KL: 'Kerala',
    LA: 'Ladakh',
    LD: 'Lakshadweep',
    MP: 'Madhya Pradesh',
    MH: 'Maharashtra',
    MN: 'Manipur',
    ML: 'Meghalaya',
    MZ: 'Mizoram',
    NL: 'Nagaland',
    OR: 'Odisha',
    OD: 'Odisha',
    PB: 'Punjab',
    RJ: 'Rajasthan',
    SK: 'Sikkim',
    TN: 'Tamil Nadu',
    TS: 'Telangana',
    TR: 'Tripura',
    UP: 'Uttar Pradesh',
    UK: 'Uttarakhand',
    WB: 'West Bengal',
    DN: 'Dadra & Nagar Haveli',
    DD: 'Daman & Diu',
    PY: 'Puducherry',
    CH: 'Chandigarh',
    DL: 'Delhi',
  };

  const stateFullNames: Record<string, string> = {
    'andhra pradesh': 'Andhra Pradesh',
    'arunachal pradesh': 'Arunachal Pradesh',
    assam: 'Assam',
    bihar: 'Bihar',
    chhattisgarh: 'Chhattisgarh',
    goa: 'Goa',
    gujarat: 'Gujarat',
    haryana: 'Haryana',
    'himachal pradesh': 'Himachal Pradesh',
    'jammu & kashmir': 'Jammu & Kashmir',
    'jammu and kashmir': 'Jammu & Kashmir',
    jharkhand: 'Jharkhand',
    karnataka: 'Karnataka',
    kerala: 'Kerala',
    ladakh: 'Ladakh',
    'lakshadweep': 'Lakshadweep',
    'madhya pradesh': 'Madhya Pradesh',
    maharashtra: 'Maharashtra',
    manipur: 'Manipur',
    meghalaya: 'Meghalaya',
    mizoram: 'Mizoram',
    nagaland: 'Nagaland',
    odisha: 'Odisha',
    punjab: 'Punjab',
    rajasthan: 'Rajasthan',
    sikkim: 'Sikkim',
    'tamil nadu': 'Tamil Nadu',
    telangana: 'Telangana',
    tripura: 'Tripura',
    'uttar pradesh': 'Uttar Pradesh',
    uttarakhand: 'Uttarakhand',
    'west bengal': 'West Bengal',
    'dadra & nagar haveli': 'Dadra & Nagar Haveli',
    'daman & diu': 'Daman & Diu',
    puducherry: 'Puducherry',
    chandigarh: 'Chandigarh',
    delhi: 'Delhi',
    'new delhi': 'Delhi',
  };

  const addressUpper = address.toUpperCase();
  const addressLower = address.toLowerCase();

  // Check abbreviations
  for (const [abbr, fullName] of Object.entries(stateAbbreviations)) {
    if (addressUpper.includes(abbr)) {
      return fullName;
    }
  }

  // Check full names
  for (const [name, fullName] of Object.entries(stateFullNames)) {
    if (addressLower.includes(name)) {
      return fullName;
    }
  }

  // Extract last word (might be state name)
  const parts = address.split(',');
  if (parts.length > 0) {
    const lastPart = parts[parts.length - 1].trim();
    const words = lastPart.split(' ');
    const lastWord = words[words.length - 1];

    if (lastWord.length >= 2) {
      return lastWord;
    }
  }

  return 'Unknown';
};

/**
 * Check if address contains a specific locality that we have coordinates for
 * Returns the most specific locality coordinates if found, null otherwise
 * Prioritizes more specific localities (like "SP Road") over general cities (like "Hyderabad")
 */
const getSpecificLocalityCoordinates = (
  address: string | null
): { latitude: number; longitude: number; locality: string } | null => {
  if (!address) return null;

  const addressLower = address.toLowerCase();

  // Priority localities - check these FIRST (most specific areas)
  // These are specific enough that we should ALWAYS use their coordinates
  const priorityLocalities = [
    'lakshmi building', 'sp road', 's p road', 'begumpet',
    'banjara hills', 'jubilee hills', 'hitech city', 'gachibowli',
    'koramangala', 'indiranagar', 'whitefield', 'electronic city',
    't nagar', 'anna nagar', 'adyar', 'velachery',
    'andheri', 'bandra', 'colaba', 'powai', 'worli',
    'connaught place', 'karol bagh', 'dwarka', 'noida',
    'salt lake', 'park street', 'cyber city', 'dlf phase',
  ];

  for (const locality of priorityLocalities) {
    if (addressLower.includes(locality)) {
      const coords = CITY_COORDINATES[locality];
      if (coords) {
        return { ...coords, locality };
      }
    }
  }

  return null;
};

/**
 * Check if coordinates are near expected city location
 * Returns true if within ~25km of expected city coordinates
 * Reduced from 100km to 25km for better accuracy
 */
const isNearExpectedCity = (
  dbLat: number,
  dbLng: number,
  address: string | null
): boolean => {
  if (!address) return true; // Can't validate without address

  const expectedCoords = getLocationCoordinates(address);

  // If expected coords are India center, we can't validate
  if (
    Math.abs(expectedCoords.latitude - INDIA_CENTER.latitude) < 0.001 &&
    Math.abs(expectedCoords.longitude - INDIA_CENTER.longitude) < 0.001
  ) {
    return true;
  }

  // Calculate approximate distance (1 degree ≈ 111km)
  const latDiff = Math.abs(dbLat - expectedCoords.latitude);
  const lngDiff = Math.abs(dbLng - expectedCoords.longitude);
  const approxDistKm = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 111;

  // If database coordinates are more than 25km from expected city, they're likely wrong
  // Reduced from 100km to 25km for better accuracy
  return approxDistKm <= 25;
};

/**
 * Normalize raw gaushala with database coordinates
 * (Scenario: Has lat/long already)
 *
 * PRIORITY ORDER:
 * 1. If address contains specific locality (Begumpet, SP Road, etc.) → use locality coordinates
 * 2. If DB coordinates are within 25km of expected city → use DB coordinates
 * 3. Otherwise → use city coordinates from address
 *
 * This ensures addresses like "SP Road | Begumpet | Secunderabad" get placed at
 * the correct Begumpet location, not at wrong database coordinates.
 */
export const normalizeGaushalaWithDbCoords = (
  gaushala: RawGaushala
): NormalizedGaushala => {
  const dbLat = gaushala.latitude!;
  const dbLng = gaushala.longitude!;
  const address = gaushala.registeredAddress;

  let finalLat = dbLat;
  let finalLng = dbLng;
  let dataSource: DataSource = 'database';
  let confidence: 'high' | 'medium' | 'low' = 'high';

  // PRIORITY 1: Check if address contains a specific locality we know
  // If address mentions "Begumpet", "SP Road", etc., use those EXACT coordinates
  const specificLocality = getSpecificLocalityCoordinates(address);

  if (specificLocality) {
    // Address has specific locality - ALWAYS use locality coordinates
    // This is more accurate than potentially wrong database coordinates
    finalLat = specificLocality.latitude;
    finalLng = specificLocality.longitude;
    dataSource = 'geocoded_from_address';
    confidence = 'high'; // High confidence because we matched a specific locality
    console.log(
      `[DataTransform] Using locality "${specificLocality.locality}" coordinates for "${gaushala.gaushalaName}": ` +
      `(${finalLat.toFixed(4)}, ${finalLng.toFixed(4)}) [Address: "${address}"]`
    );
  } else {
    // PRIORITY 2: Validate database coordinates against expected city
    const isValidLocation = isNearExpectedCity(dbLat, dbLng, address);

    if (!isValidLocation) {
      // Database coordinates are wrong (>25km from expected) - use city coordinates
      const cityCoords = getLocationCoordinates(address);
      finalLat = cityCoords.latitude;
      finalLng = cityCoords.longitude;
      dataSource = 'geocoded_from_address';
      confidence = 'medium';
      console.log(
        `[DataTransform] Corrected wrong DB coordinates for "${gaushala.gaushalaName}": ` +
        `DB(${dbLat?.toFixed(4) ?? 'N/A'}, ${dbLng?.toFixed(4) ?? 'N/A'}) → City(${finalLat.toFixed(4)}, ${finalLng.toFixed(4)})`
      );
    } else {
      // Database coordinates are valid - use them
      console.log(
        `[DataTransform] Using DB coordinates for "${gaushala.gaushalaName}": ` +
        `(${finalLat.toFixed(4)}, ${finalLng.toFixed(4)}) at "${address}"`
      );
    }
  }

  return {
    id: gaushala.id,
    name: gaushala.gaushalaName,
    address: gaushala.registeredAddress || 'Address not provided',
    pincode: gaushala.pincode || null,
    latitude: finalLat,
    longitude: finalLng,
    dataSource,
    confidence,
    metadata: {
      totalCattle: gaushala.totalCattle,
      milkingCows: gaushala.milkingCows,
      totalLandArea: gaushala.totalLandArea || null,
      status: gaushala.status,
      createdAt: gaushala.createdAt,
    },
  };
};

/**
 * Normalize raw gaushala with geocoding result
 * (Scenario: Geocoded from pincode/address/name)
 */
export const normalizeGaushalaWithGeocoding = (
  gaushala: RawGaushala,
  geoResult: GeocodingResult
): NormalizedGaushala => {
  // Determine data source from geocoding result
  let dataSource: DataSource = 'geocoded_from_address';

  if (geoResult.source.includes('pincode')) {
    dataSource = 'geocoded_from_pincode';
  } else if (geoResult.source.includes('name')) {
    dataSource = 'geocoded_from_name';
  } else if (geoResult.source.includes('address')) {
    dataSource = 'geocoded_from_address';
  }

  return {
    id: gaushala.id,
    name: gaushala.gaushalaName,
    address: gaushala.registeredAddress || geoResult.address || 'Address not available',
    pincode: gaushala.pincode || null,
    latitude: geoResult.latitude,
    longitude: geoResult.longitude,
    dataSource,
    confidence: geoResult.confidence,
    metadata: {
      totalCattle: gaushala.totalCattle,
      milkingCows: gaushala.milkingCows,
      totalLandArea: gaushala.totalLandArea || null,
      status: gaushala.status,
      createdAt: gaushala.createdAt,
    },
  };
};

/**
 * Get location coordinates (city first, then state, then India center)
 * EXPORTED for use in geocoding service fallback logic
 * IMPROVED: State-aware city matching to prevent cross-state false matches
 * NOTE: Does NOT strictly validate address - that's done in isValidAddress()
 */
export const getLocationCoordinates = (address: string | null): { latitude: number; longitude: number } => {
  if (!address) return INDIA_CENTER;

  const addressLower = address.trim().toLowerCase();

  // FIRST: Extract state from address (this gives us context)
  const state = extractStateFromAddress(address);

  // SECOND: Look for cities in CITY_COORDINATES directly (includes all variants)
  // Sort by length (longest first) to avoid substring mismatches
  // E.g., "visakhapatnam" (14 chars) should match before "patna" (5 chars)
  const allCities = Object.keys(CITY_COORDINATES).sort((a, b) => b.length - a.length);

  for (const city of allCities) {
    if (addressLower.includes(city)) {
      const coords = CITY_COORDINATES[city];
      if (coords) {
        return coords;
      }
    }
  }

  // THIRD: If no city found in the state, fall back to state coordinates
  const stateCoords = getStateCoordinates(state);
  return stateCoords;
};

/**
 * Normalize raw gaushala with fallback coordinates
 * (Scenario: All geocoding methods failed)
 * Uses city-specific coordinates, then state-specific, then India center
 */
export const normalizeGaushalaWithFallback = (
  gaushala: RawGaushala
): NormalizedGaushala => {
  // Extract city/state from address and get precise coordinates
  const locationCoords = getLocationCoordinates(gaushala.registeredAddress);

  return {
    id: gaushala.id,
    name: gaushala.gaushalaName,
    address: gaushala.registeredAddress || 'Address not provided',
    pincode: gaushala.pincode || null,
    latitude: locationCoords.latitude,
    longitude: locationCoords.longitude,
    dataSource: 'fallback',
    confidence: 'low',
    metadata: {
      totalCattle: gaushala.totalCattle,
      milkingCows: gaushala.milkingCows,
      totalLandArea: gaushala.totalLandArea || null,
      status: gaushala.status,
      createdAt: gaushala.createdAt,
    },
  };
};

/**
 * Separate gaushalas into two groups:
 * - Those with coordinates (no geocoding needed)
 * - Those without coordinates (need geocoding)
 */
export const separateGaushalas = (gaushalas: RawGaushala[]) => {
  const withCoordinates: RawGaushala[] = [];
  const needsGeocoding: RawGaushala[] = [];

  for (const gaushala of gaushalas) {
    // Use loose != to treat both null AND undefined as "needs geocoding"
    // The API omits latitude/longitude when NULL in the DB, resulting in undefined
    if (gaushala.latitude != null && gaushala.longitude != null) {
      withCoordinates.push(gaushala);
    } else {
      needsGeocoding.push(gaushala);
    }
  }

  return { withCoordinates, needsGeocoding };
};

/**
 * Calculate state-wise statistics from normalized gaushalas
 */
export const calculateStateStatistics = (
  gaushalas: NormalizedGaushala[]
): Record<string, number> => {
  const stats: Record<string, number> = {};

  for (const gaushala of gaushalas) {
    const state = extractStateFromAddress(gaushala.address);

    if (!stats[state]) {
      stats[state] = 0;
    }
    stats[state]++;
  }

  return stats;
};

/**
 * Calculate geocoding statistics
 */
export const calculateGeocodingStatistics = (
  gaushalas: NormalizedGaushala[]
) => {
  const stats = {
    total: gaushalas.length,
    fromDatabase: 0,
    geocoded: 0,
    bySource: {} as Record<string, number>,
  };

  for (const gaushala of gaushalas) {
    if (gaushala.dataSource === 'database') {
      stats.fromDatabase++;
    } else {
      stats.geocoded++;
    }

    const source = gaushala.dataSource;
    if (!stats.bySource[source]) {
      stats.bySource[source] = 0;
    }
    stats.bySource[source]++;
  }

  return stats;
};

/**
 * Build geocoding queue from gaushalas needing coordinates
 */
export const buildGeocodingQueue = (
  gaushalas: RawGaushala[]
): Array<{ gaushala: RawGaushala; pincode: string | null; address: string | null; name: string | null }> => {
  return gaushalas.map((gaushala) => ({
    gaushala,
    pincode: gaushala.pincode,
    address: gaushala.registeredAddress,
    name: gaushala.gaushalaName,
  }));
};

/**
 * Merge normalized gaushalas with geocoding results
 */
export const mergeWithGeocodingResults = (
  gaushalas: RawGaushala[],
  geoResults: GeocodingResult[]
): NormalizedGaushala[] => {
  if (gaushalas.length !== geoResults.length) {
    console.error(
      `Mismatch: ${gaushalas.length} gaushalas, ${geoResults.length} results`
    );
  }

  return gaushalas.map((gaushala, index) => {
    const geoResult = geoResults[index] || {
      latitude: INDIA_CENTER.latitude,
      longitude: INDIA_CENTER.longitude,
      confidence: 'low' as const,
      source: 'fallback',
    };

    return normalizeGaushalaWithGeocoding(gaushala, geoResult);
  });
};

/**
 * Validate coordinate is within India bounds
 */
const INDIA_BOUNDS = {
  minLat: 8.0,
  maxLat: 35.0,
  minLng: 68.0,
  maxLng: 97.0,
};

export const isValidIndianCoordinate = (
  latitude: number,
  longitude: number
): boolean => {
  return (
    latitude >= INDIA_BOUNDS.minLat &&
    latitude <= INDIA_BOUNDS.maxLat &&
    longitude >= INDIA_BOUNDS.minLng &&
    longitude <= INDIA_BOUNDS.maxLng
  );
};

/**
 * Validate raw gaushala data
 */
export const validateGaushalaData = (
  gaushala: RawGaushala
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!gaushala.id) {
    errors.push('Missing gaushala ID');
  }

  if (!gaushala.gaushalaName || gaushala.gaushalaName.trim().length === 0) {
    errors.push('Missing gaushala name');
  }

  // At least one location data should exist
  const hasLatLong =
    gaushala.latitude !== null && gaushala.longitude !== null;
  const hasAddress =
    gaushala.registeredAddress && gaushala.registeredAddress.trim().length > 0;
  const hasPincode = gaushala.pincode && gaushala.pincode.trim().length > 0;

  if (!hasLatLong && !hasAddress && !hasPincode) {
    errors.push('No location data available (no address, pincode, or coordinates)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Sort gaushalas by state
 */
export const sortGaushalas = (
  gaushalas: NormalizedGaushala[],
  sortBy: 'state' | 'name' | 'date' = 'state'
): NormalizedGaushala[] => {
  const sorted = [...gaushalas];

  switch (sortBy) {
    case 'state': {
      sorted.sort((a, b) => {
        const stateA = extractStateFromAddress(a.address);
        const stateB = extractStateFromAddress(b.address);
        return stateA.localeCompare(stateB);
      });
      break;
    }
    case 'name': {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    }
    case 'date': {
      sorted.sort(
        (a, b) =>
          new Date(b.metadata.createdAt).getTime() -
          new Date(a.metadata.createdAt).getTime()
      );
      break;
    }
  }

  return sorted;
};

/**
 * Spread overlapping markers by adding small offsets
 * This ensures all markers at the same location are visible
 */
export const spreadOverlappingMarkers = (
  gaushalas: NormalizedGaushala[]
): NormalizedGaushala[] => {
  // Group gaushalas by their coordinates (rounded to 4 decimal places)
  const coordGroups: Map<string, NormalizedGaushala[]> = new Map();

  gaushalas.forEach((g) => {
    const key = `${g.latitude.toFixed(4)},${g.longitude.toFixed(4)}`;
    if (!coordGroups.has(key)) {
      coordGroups.set(key, []);
    }
    coordGroups.get(key)!.push(g);
  });

  // Apply offset to markers at same location
  const result: NormalizedGaushala[] = [];

  coordGroups.forEach((group) => {
    if (group.length === 1) {
      // Single marker, no offset needed
      result.push(group[0]);
    } else {
      // Multiple markers at same location - spread them in a circle
      // Using offset of 0.015 ≈ 1.5km to make markers clearly visible
      const offsetDistance = 0.015; // ~1.5km offset between markers
      const angleStep = (2 * Math.PI) / group.length;

      group.forEach((gaushala, index) => {
        const angle = index * angleStep;
        const offsetLat = offsetDistance * Math.cos(angle);
        const offsetLng = offsetDistance * Math.sin(angle);

        result.push({
          ...gaushala,
          latitude: gaushala.latitude + offsetLat,
          longitude: gaushala.longitude + offsetLng,
        });
      });

      console.log(
        `[Markers] Spread ${group.length} overlapping markers at ${group[0].address}`
      );
    }
  });

  return result;
};
